import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { BASE_URL } from "../config";
import { getAuthItem } from "../utils/auth";

/** Matches crm_atlas_backend/utils/appAcl.js */
export const APP_PERMISSION_KEYS = [
  "accounting",
  "admin",
  "system",
  "client",
  "preferences",
  "officememployee",
  "employeecandidate",
  "shiftactual",
  "shift",
];

const READ_LEVEL = 5;
const WRITE_LEVEL = 10;
/** Matches Admin_Accounts.js accessMap.full and appAcl.js FULL_ACCESS */
export const FULL_LEVEL = 15;

function emptyPermissions() {
  return Object.fromEntries(APP_PERMISSION_KEYS.map((k) => [k, 0]));
}

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const [permissions, setPermissions] = useState(emptyPermissions());
  const [allPermissions, setAllPermissions] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const token = getAuthItem("token");
    if (!token || token === "null" || token === "undefined") {
      setPermissions(emptyPermissions());
      setAllPermissions(false);
      setIsSuperAdmin(false);
      setLoaded(true);
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}/api/acl/app-permissions/me`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("app-permissions failed");
      const j = await res.json();
      const p = j?.data?.permissions || {};
      setPermissions({ ...emptyPermissions(), ...p });
      setAllPermissions(!!j?.data?.allPermissions);
      setIsSuperAdmin(!!j?.data?.isSuperAdmin);
    } catch {
      setPermissions(emptyPermissions());
      setAllPermissions(false);
      setIsSuperAdmin(false);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
    const onAuth = () => {
      load();
    };
    window.addEventListener("authUpdate", onAuth);
    return () => window.removeEventListener("authUpdate", onAuth);
  }, [load]);

  const value = useMemo(() => {
    const access = (key) => Number(permissions[key] ?? 0) || 0;
    const canRead = (key) => access(key) >= READ_LEVEL;
    const canWrite = (key) => access(key) >= WRITE_LEVEL;
    return {
      permissions,
      allPermissions,
      isSuperAdmin,
      canSeeRevenue: isSuperAdmin,
      loaded,
      reload: load,
      access,
      canRead,
      canWrite,
      canAccessPersonnel:
        canRead("officememployee") || canRead("employeecandidate"),
      canReadOfficeStaff: canRead("officememployee"),
      canWriteOfficeStaff: canWrite("officememployee"),
      canReadTempStaff: canRead("employeecandidate"),
      canWriteTempStaff: canWrite("employeecandidate"),
      canReadClients: canRead("client"),
      canWriteClients: canWrite("client"),
      canAddClientPreference: canRead("client"),
      canEditClientPreference: canWrite("preferences"),
      canReadAccounting: canRead("accounting"),
      canWriteAccounting: canWrite("accounting"),
      canReadAdmin: canRead("admin"),
      canWriteAdmin: canWrite("admin"),
      canFullAdmin: allPermissions || access("admin") >= FULL_LEVEL,
      canReadShifts: canRead("shift"),
      canWriteShifts: canWrite("shift"),
      canReadShiftActual: canRead("shiftactual"),
      canWriteShiftActual: canWrite("shiftactual"),
    };
  }, [permissions, allPermissions, isSuperAdmin, loaded, load]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

export function useAppPermissions() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error("useAppPermissions must be used within PermissionProvider");
  }
  return ctx;
}

/** Safe for optional use outside main shell (e.g. tests) — returns permissive defaults. */
export function useAppPermissionsOptional() {
  return useContext(PermissionContext);
}
